import type { ArtMetadata, ArtTextStyle, Bind } from "../schedule-presets/art-metadata";
import type { MetadataEventPayload } from "../metadata-history/metadata-events";
import type { MetadataDay } from "../metadata-history/metadata-targets";
import type { BuilderArtwork } from "./artwork";
import type { StreamLayout } from "./day-layouts";
import { artworkPlacement } from "./artwork-placement";
import { defaultElementPosition } from "../schedule-art/layout-coordinates";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { createStreamElement, requiredStreamBindings, streamBindings, type StreamNumber } from "./stream-layout";

export function createTwoSlotLayout(metadata: ArtMetadata, artwork: BuilderArtwork): MetadataEventPayload {
  const placements = artwork.double.map(artworkPlacement);
  const anchors = Object.fromEntries(weekdayKeys.map((day, index) => [day, placements[index]!.point])) as StreamLayout["anchors"];
  // Simple starting positions; the user places both streams on the b artwork.
  const defaults = ([1, 2] as const).flatMap(number => {
    const titleOffset = defaultElementPosition([placements[0]!.suggested ? 24 : 0, number === 1 ? 120 : 320]);
    return requiredStreamBindings(number).map(bind => createStreamElement(metadata, "two_slot", bind, titleOffset));
  });
  const styles = Object.fromEntries(defaults.flatMap(entry => Object.entries(entry.styles)));
  const formats = Object.fromEntries(defaults.flatMap(({ element }) => element.format ? [[element.format, "%H:%M"]] : []));
  const layout: StreamLayout = { mode: "two_slot", anchors, shared: defaults.map(entry => entry.element), exceptions: {} };
  return { type: "day-layout/set", layout, styles, formats, action: "initialize" };
}

export function copyStreamLayout(metadata: ArtMetadata, layout: StreamLayout, day: MetadataDay, destination: StreamNumber): MetadataEventPayload {
  const source: StreamNumber = destination === 1 ? 2 : 1;
  const next = structuredClone(layout);
  const custom = !!next.exceptions[day];
  const elements = next.exceptions[day] ?? next.shared;
  const sourceElements = elements.filter(element => element.bind.startsWith(`slot${source}.`));
  const sourceTitle = sourceElements.find(element => element.bind === `slot${source}.title`);
  const destinationTitle = elements.find(element => element.bind === `slot${destination}.title`);
  const sourceOffset = sourceTitle?.offset ?? [0, 0];
  const destinationOffset = destinationTitle?.offset ?? defaultElementPosition([24, destination === 1 ? 120 : 320]);
  const delta = [destinationOffset[0]! - sourceOffset[0]!, destinationOffset[1]! - sourceOffset[1]!];
  const styles: Record<string, ArtTextStyle> = {};
  const formats: Record<string, string> = {};
  const copied = sourceElements.map(element => {
    const bind = element.bind.replace(`slot${source}.`, `slot${destination}.`) as Bind;
    const name = `builder_${layout.mode}_${custom ? `${day}_` : ""}${bind.replace(".", "_")}`;
    const target = { ...element, bind, offset: [element.offset[0]! + delta[0]!, element.offset[1]! + delta[1]!] };
    if (element.style && metadata.styles[element.style]) {
      styles[name] = structuredClone(metadata.styles[element.style]!);
      target.style = name;
    }
    if (element.format && metadata.formats[element.format] !== undefined) {
      target.format = `${name}_format`;
      formats[target.format] = metadata.formats[element.format]!;
    }
    return target;
  });
  const kept = elements.filter(element => !element.bind.startsWith(`slot${destination}.`));
  const bindings = [...streamBindings(1), ...streamBindings(2)];
  const updated = [...kept, ...copied].sort((a, b) => bindings.indexOf(a.bind) - bindings.indexOf(b.bind));
  if (custom) next.exceptions[day] = updated;
  else next.shared = updated;
  return { type: "day-layout/set", layout: next, styles, formats, action: "copy", day: custom ? day : undefined };
}
